import { memo } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { usePartnershipData } from './usePartnershipData.jsx';
import { combineRawPartnerTexts } from '../../../utils/dataProcessor/partnershipParsers.js';

export const PartnerCoverageCallout = memo(function PartnerCoverageCallout({ results: resultsProp }) {
  const { localFilteredResults } = usePartnershipData();
  const results = resultsProp ?? localFilteredResults;

  const noDataFallback = (
    <ChartWrapper title="Partner Coverage by Result Type">
      <NoChartData message="No partner coverage data available" detail="This callout shows partner type coverage across delivery types (Innovation, Scaling, Demand). No partner data with delivery types was found in the current selection." />
    </ChartWrapper>
  );

  if (!results || results.length === 0) return noDataFallback;

  const byType = {};
  results.forEach(r => {
    if (!r.type) return;
    if (!byType[r.type]) byType[r.type] = { total: 0, withPartners: 0 };
    byType[r.type].total++;
    const partnerText = combineRawPartnerTexts(r.partners, r.partnersKP);
    if (partnerText) {
      byType[r.type].withPartners++;
    }
  });

  const rows = Object.entries(byType)
    .map(([type, data]) => ({
      type,
      total: data.total,
      withPartners: data.withPartners,
      coverage: data.total > 0 ? Math.round((data.withPartners / data.total) * 100) : 0,
    }))
    .sort((a, b) => b.coverage - a.coverage);

  if (rows.length === 0) return noDataFallback;

  const chartData = rows.map(r => ({ Type: r.type, 'With Partners': r.withPartners, Total: r.total, 'Coverage %': r.coverage }));

  const totalAll = rows.reduce((s, r) => s + r.total, 0);
  const totalWithPartners = rows.reduce((s, r) => s + r.withPartners, 0);
  const overallCoverage = totalAll > 0 ? Math.round((totalWithPartners / totalAll) * 100) : 0;

  const getBarColor = (pct) => pct >= 50 ? '#006837' : pct >= 25 ? '#F37021' : '#94a3b8';

  return (
    <ChartWrapper title="Partner Coverage by Result Type" info="Percentage of results that have at least one external partner, broken down by result type. Higher coverage indicates stronger partner engagement." chartData={chartData} sourceResults={results} exportFilename="partner-coverage">
      <p className="text-sm text-gray-500 -mt-1 mb-3">Percentage of results with at least one external partner, by result type</p>

      {/* Summary metrics */}
      <div className="flex gap-10 mb-5">
        <div>
          <div className="text-2xl font-bold text-gray-900">{totalAll.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Total Results</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{totalWithPartners.toLocaleString()}</div>
          <div className="text-xs text-gray-500">With Partners</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{overallCoverage}%</div>
          <div className="text-xs text-gray-500">Overall Coverage</div>
        </div>
      </div>

      {/* Coverage bars */}
      <div className="space-y-2">
        {rows.map(row => {
          const color = getBarColor(row.coverage);
          return (
            <div key={row.type} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <div className="w-28 sm:w-40 text-sm text-gray-600 truncate" title={row.type}>{row.type}</div>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                {[25, 50, 75].map(tick => (
                  <div key={tick} className="absolute top-0 bottom-0 w-px bg-gray-300/40" style={{ left: `${tick}%` }} />
                ))}
                <div className="h-full rounded-full transition-all" style={{ width: `${row.coverage}%`, backgroundColor: color }} />
              </div>
              <div className="w-24 text-xs font-medium text-gray-900 text-right">
                {row.coverage}% ({row.withPartners}/{row.total})
              </div>
            </div>
          );
        })}
      </div>

    </ChartWrapper>
  );
});
